import * as React from 'react';
import { setConfig } from 'react-hot-loader';
import { hot } from 'react-hot-loader/root';

import * as digitalData from '@ao-internal/digital-data';
import useTimeout from '@rooks/use-timeout';

import AppErrorFallback from './components/AppErrorFallback';
import ChatWindow from './components/ChatWindow';
import ErrorBoundary from './components/ErrorBoundary';
import ToastNotification from './components/ToastNotification';
import ToggleButton from './components/ToggleButton';
import { MvtConfig } from './config/types';
import {
	CONVERSATION_STARTER,
	TAGGING_CLICK_EVENT_NAME,
	TaggingEventNames,
	VISIBILITY_CHANGE_EVENT_NAME,
	originalDocumentTitle,
	tabId,
} from './globals';
import { windowBreakpoint } from './helpers/breakpoint';
import BreakpointObserver, {
	BREAKPOINT_CHANGED,
} from './helpers/breakpoint-observer';
import { shouldRetrieveRemoteSession } from './helpers/storage';
import * as tabManager from './helpers/tab';
import useAnalytics from './hooks/useAnalytics';
import useChat from './hooks/useChat';
import useLogger from './hooks/useLogger';
import {
	ChatMode,
	ChatWindowStatusTypes,
	StoreProvider,
	useChatDispatch,
	useChatState,
} from './store';
import { InputTypes, State } from './store';

const hasBreakpointObserver = !windowBreakpoint;
let breakpointObserver: BreakpointObserver;
let checkIsActiveTabTimeoutRef: number;
let hasActivated = true;

if (hasBreakpointObserver) {
	breakpointObserver = new BreakpointObserver();
}

// TODO: Move this type to the state helpers (closer to getChatSession)
export type ChatSession = Pick<
	State,
	| 'sessionId'
	| 'results'
	| 'chatInitialised'
	| 'chatMode'
	| 'chatHistory'
	| 'liveChatStatus'
	| 'liveChatAgent'
	| 'liveChatActivationData'
	| 'liveChatAutoDisconnectNoticeReceivedTimestamp'
	| 'liveChatAutoDisconnectNoticeDismissedTimestamp'
	| 'lastPageUpdate'
	| 'toastNotification'
>;

interface BreakpointChangeEvent extends CustomEvent {
	breakpoint: string;
	previousBreakpoint: string;
}

interface AppProps extends React.HTMLProps<HTMLDivElement> {
	mvtConfig?: MvtConfig;
	chatSession: ChatSession;
}

export const App: React.FC<AppProps> = ({ mvtConfig = {}, chatSession }) => {
	const {
		hideToggleCta,
		toggleCtaVariant,
		toggleCtaRenderDelay,
		chatWindowVariant,
	} = mvtConfig;
	const { initSession, initialiseChat, sendMessage, refreshChatSession } =
		useChat();
	const {
		setChatInitialised,
		setChatWindowOpen,
		setChatWindowStatus,
		setBreakpoint,
		setIsTabVisible,
		setIsActiveTab,
		setShowLiveChatAutoDisconnectNotice,
		setToastOpen,
		setToastClose,
		createToast,
	} = useChatDispatch();
	const globalState = useChatState();
	const {
		sessionId,
		chatInitialised,
		chatWindowOpen,
		chatWindowStatus,
		chatHistory,
		chatMode,
		isActiveTab,
		liveChatStatus,
		liveChatExitedBy,
		liveChatAutoDisconnectNoticeReceivedTimestamp,
		liveChatAutoDisconnectNoticeDismissedTimestamp,
		showLiveChatAutoDisconnectNotice,
		liveChatActivationData,
		liveChatAgent,
		toastNotification,
		toastNotificationDismissed,
		toastNotificationsLog,
	} = globalState;

	useAnalytics({
		chatMode,
		chatHistory,
		liveChatStatus,
		liveChatExitedBy,
		showLiveChatAutoDisconnectNotice,
		liveChatActivationData,
		liveChatAgent,
	});
	useLogger(globalState);

	const [initialMessage, setInitialMessage] =
		React.useState(CONVERSATION_STARTER);
	const [messageInputType, setMessageInputType] = React.useState<InputTypes>(
		InputTypes.Text
	);
	const [processTaggingEvent, setProcessTaggingEvent] = React.useState(false);
	const [isSystemMessage, setIsSystemMessage] = React.useState(true);
	const [renderToggleCta, setRenderToggleCta] = React.useState(false);
	const {
		start: renderToggleCtaTimeoutStart,
		clear: renderToggleCtaTimeoutClear,
	} = useTimeout(
		() => {
			// The toggle CTA should never be hidden if a toast notification exists
			if (!hideToggleCta || toastNotification) {
				setRenderToggleCta(true);
			}
		},
		toggleCtaRenderDelay && !hideToggleCta ? toggleCtaRenderDelay : 0
	);
	const shouldRenderToastNotification = React.useMemo<boolean>(() => {
		if (
			!chatWindowOpen &&
			!toastNotificationDismissed &&
			toastNotification &&
			typeof toastNotification !== 'boolean'
		) {
			const currentToastLog =
				toastNotification && toastNotificationsLog
					? toastNotificationsLog[toastNotification.namespace]
					: undefined;

			if (!toastNotification) {
				return false;
			}

			if (!toastNotification.dismissTimeToLive) {
				return true;
			}

			if (
				currentToastLog &&
				currentToastLog.dismissedTimestamp !== undefined &&
				toastNotification.dismissTimeToLive
			) {
				return Date.now() - currentToastLog.dismissedTimestamp >
					toastNotification.dismissTimeToLive
					? true
					: false;
			}

			return true;
		}

		return false;
	}, [
		chatWindowOpen,
		toastNotification,
		toastNotificationDismissed,
		toastNotificationsLog,
	]);

	const refreshChat = React.useCallback(() => {
		// Reset states
		setChatWindowStatus(ChatWindowStatusTypes.Loading);
		setProcessTaggingEvent(false);

		setChatInitialised(false);
	}, [setChatWindowStatus, setProcessTaggingEvent, setChatInitialised]);

	function handleBreakpointChange(evt: BreakpointChangeEvent): void {
		if (!evt.breakpoint) {
			setBreakpoint(evt.detail.breakpoint);
		} else {
			setBreakpoint(evt.breakpoint);
		}
	}

	function toggleChatWindow() {
		digitalData.sendBeacon(TaggingEventNames.interaction, {
			type: 'click',
			element: chatWindowOpen ? 'close-chat-window' : 'open-chat-window',
		});

		setChatWindowOpen(!chatWindowOpen);
	}

	function handleToggleButtonClick() {
		setMessageInputType(InputTypes.Interaction);
		toggleChatWindow();
	}

	function onToastOpen() {
		if (toastNotification) {
			setToastOpen(toastNotification.namespace);

			digitalData.sendBeacon(TaggingEventNames.toastNotificationOnOpen, {
				label: `display ${toastNotification.namespace} toast notification`,
			});
		}
	}

	function onToastClose() {
		if (toastNotification) {
			setToastClose(toastNotification.namespace);

			digitalData.sendBeacon(TaggingEventNames.toastNotificationOnDismiss, {
				label: `dismiss ${toastNotification.namespace} toast notification`,
			});
		}
	}

	function pageTaggingEventHandler(event: CustomEvent) {
		// User has clicked a page element. We need to decide whether it needs the chatbot to step in
		if (event.detail && event.detail['ao-assistant']) {
			const message = event.detail['ao-assistant'];
			const sendAsSystem = event.detail['ao-assistant-system'];
			const isInteraction = event.detail['ao-assistant-interaction'];
			const isFreeText = event.detail['ao-assistant-free-text'];

			if (!chatWindowOpen) {
				digitalData.sendBeacon(TaggingEventNames.interaction, {
					type: 'click',
					element: 'open-chat-window',
				});
			}

			const isSystemMsg =
				message === CONVERSATION_STARTER || sendAsSystem ? true : false;

			let inputType = isSystemMsg
				? InputTypes.Undefined
				: InputTypes.Interaction;

			if (isInteraction) {
				inputType = InputTypes.Interaction;
			}

			if (isFreeText) {
				inputType = InputTypes.Text;
			}

			setInitialMessage(message);
			setIsSystemMessage(isSystemMsg);
			setMessageInputType(inputType);
			setChatWindowOpen(true);
			setProcessTaggingEvent(true);
		}
	}

	const activate = React.useCallback(async () => {
		hasActivated = true;

		if (shouldRetrieveRemoteSession()) {
			hasActivated = true;
			refreshChatSession();
		}
	}, [refreshChatSession]);

	async function checkIsActiveTab() {
		clearTimeout(checkIsActiveTabTimeoutRef);
		checkIsActiveTabTimeoutRef = window.setTimeout(checkIsActiveTab, 5000);

		const shouldBeActive = tabManager.isActiveTab(tabId);
		if (shouldBeActive) {
			setIsActiveTab(true);
			if (!hasActivated) {
				activate();
			}
		} else {
			setIsActiveTab(false);
			hasActivated = false;
		}
	}

	async function handleVisibilityChange() {
		if (document.visibilityState === 'visible') {
			clearTimeout(checkIsActiveTabTimeoutRef);
			document.title = originalDocumentTitle;
			tabManager.registerActiveTab(tabId);
			setIsTabVisible(true);
			// if (!hasActivated) {
			activate();
			// }
		} else {
			setIsTabVisible(false);
			checkIsActiveTab();
		}
	}

	React.useEffect(() => {
		// Event listeners create a closure around variables so a snapshot of globalState is created
		// which will make it out of date when the event callback is run. We need to add and remove
		// when globalState is changed.
		// See: https://github.com/facebook/react/issues/17783

		document.addEventListener(
			VISIBILITY_CHANGE_EVENT_NAME,
			handleVisibilityChange as EventListener
		);

		return () => {
			hasActivated = true;

			document.removeEventListener(
				VISIBILITY_CHANGE_EVENT_NAME,
				handleVisibilityChange as EventListener
			);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [globalState]);

	React.useEffect(() => {
		tabManager.registerActiveTab(tabId);
		initSession(chatSession);

		// When the user clicks or interacts with tagged page elements
		document.body.addEventListener(
			TAGGING_CLICK_EVENT_NAME,
			pageTaggingEventHandler as EventListener
		);

		document.documentElement.classList.add('chatbot-widget');

		renderToggleCtaTimeoutStart();

		digitalData.manipulateDigitalData({
			page: {
				pageInfo: {
					versions: {
						chatbot: VERSION_NUMBER,
					},
					chatbot: {
						sessionId: chatSession.sessionId,
					},
				},
			},
		});

		if (hasBreakpointObserver) {
			// The dev site emits the "breakpointChanged" event on the breakpointObserver class object
			setBreakpoint(breakpointObserver.value);
			breakpointObserver.addEventListener(
				BREAKPOINT_CHANGED,
				handleBreakpointChange as EventListener
			);
		} else {
			// The live site emits the "breakpointChanged" event on the window
			setBreakpoint(windowBreakpoint.value);
			window.addEventListener(
				BREAKPOINT_CHANGED,
				handleBreakpointChange as EventListener
			);
		}

		return () => {
			if (hasBreakpointObserver) {
				breakpointObserver.unlisten();
			} else if (breakpointObserver) {
				window.removeEventListener(
					BREAKPOINT_CHANGED,
					handleBreakpointChange as EventListener
				);
			}

			renderToggleCtaTimeoutClear();

			document.body.removeEventListener(
				TAGGING_CLICK_EVENT_NAME,
				pageTaggingEventHandler as EventListener
			);
		};
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	React.useEffect(() => {
		if (isActiveTab && !hasActivated) {
			activate();
		}
	}, [activate, isActiveTab]);

	React.useEffect(() => {
		if (chatInitialised && chatHistory.length && processTaggingEvent) {
			if (!chatWindowOpen) {
				digitalData.sendBeacon(TaggingEventNames.interaction, {
					type: 'click',
					element: 'open-chat-window',
				});
			}

			sendMessage(initialMessage, {
				addToHistory: !isSystemMessage,
				isSystemMessage,
				inputType: messageInputType,
			});

			setProcessTaggingEvent(false);
			setMessageInputType(InputTypes.Text);
		}
	}, [
		chatHistory.length,
		chatInitialised,
		chatWindowOpen,
		initialMessage,
		isSystemMessage,
		messageInputType,
		processTaggingEvent,
		sendMessage,
	]);

	React.useEffect(() => {
		if (!chatWindowOpen && chatWindowStatus === ChatWindowStatusTypes.Error) {
			refreshChat();
		}
	}, [chatWindowOpen, chatWindowStatus, refreshChat]);

	React.useEffect(() => {
		digitalData.manipulateDigitalData({
			page: {
				pageInfo: {
					chatbot: {
						available: true,
						open: chatWindowOpen,
					},
				},
			},
		});
	}, [chatWindowOpen]);

	React.useEffect(() => {
		if (chatWindowOpen) {
			setRenderToggleCta(true);
			if (toastNotification) {
				setToastClose(toastNotification.namespace);
			}
		}
	}, [chatWindowOpen, setToastClose, toastNotification]);

	React.useEffect(() => {
		if (document.documentElement) {
			if (chatWindowOpen) {
				document.documentElement.classList.add('chatbot-widget--open');
			} else {
				document.documentElement.classList.remove('chatbot-widget--open');
			}
		}
	}, [chatWindowOpen]);

	React.useEffect(() => {
		async function doInit() {
			await initialiseChat(initialMessage, isSystemMessage, messageInputType);
			setMessageInputType(InputTypes.Text);
		}

		if (!chatInitialised && chatWindowOpen) {
			setProcessTaggingEvent(false);
			doInit();
		}
	}, [
		chatInitialised,
		chatWindowOpen,
		initialMessage,
		initialiseChat,
		isSystemMessage,
		messageInputType,
	]);

	React.useEffect(() => {
		let showNotice = false;
		if (
			chatMode === ChatMode.LiveChat &&
			liveChatAutoDisconnectNoticeReceivedTimestamp
		) {
			if (
				!liveChatAutoDisconnectNoticeDismissedTimestamp ||
				liveChatAutoDisconnectNoticeDismissedTimestamp <
					liveChatAutoDisconnectNoticeReceivedTimestamp
			) {
				showNotice = true;
			}
		}
		setShowLiveChatAutoDisconnectNotice(showNotice);
		digitalData.manipulateDigitalData({
			page: {
				pageInfo: {
					chatbot: {
						liveChatAutoDisconnectNoticeDisplayed: showNotice,
					},
				},
			},
		});
	}, [
		chatMode,
		liveChatAutoDisconnectNoticeDismissedTimestamp,
		liveChatAutoDisconnectNoticeReceivedTimestamp,
		setShowLiveChatAutoDisconnectNotice,
	]);

	React.useEffect(() => {
		let showNotice = false;

		if (!chatWindowOpen) {
			if (showLiveChatAutoDisconnectNotice) {
				showNotice = true;
			}
		}

		if (showNotice) {
			createToast({
				namespace: 'auto disconnect',
				content: 'Live chat will end if we <br/>don&#39;t hear from you soon',
				waitTime: 0,
				dismissTimeToLive: 0,
			});
		}
	}, [chatWindowOpen, createToast, showLiveChatAutoDisconnectNotice]);

	return (
		<React.Fragment>
			<h5 id="chatbot-title" className="sr-only">
				Chatbot window
			</h5>

			{shouldRenderToastNotification && toastNotification ? (
				<ToastNotification
					data-testid={`chatbot-${toastNotification.namespace
						.replace(/\s+/g, '-')
						.toLowerCase()}-toast-notification`}
					data-tag-area={`${toastNotification.namespace} toast notification`}
					waitTime={
						toastNotification.waitTime !== undefined
							? toastNotification.waitTime
							: 2500
					}
					scrollToPosY={0}
					onOpen={onToastOpen}
					onClose={onToastClose}
				>
					{toastNotification.content ? (
						<div
							// eslint-disable-next-line react/no-danger
							dangerouslySetInnerHTML={{ __html: toastNotification.content }}
						/>
					) : (
						<React.Fragment>
							Have a question? <br className="md:hidden" />
							<strong>Let me help</strong>
						</React.Fragment>
					)}
				</ToastNotification>
			) : null}

			{chatWindowOpen && (
				<ChatWindow
					variant={chatWindowVariant}
					toggleChatWindow={toggleChatWindow}
					refreshChat={refreshChat}
					status={chatWindowStatus}
				/>
			)}

			{renderToggleCta && (
				<ToggleButton
					onClick={handleToggleButtonClick}
					isOpen={chatWindowOpen}
					variant={toggleCtaVariant}
					chatMode={chatMode}
				/>
			)}
		</React.Fragment>
	);
};

export const AppWithProviders: React.FunctionComponent<AppProps> = ({
	mvtConfig = {},
	chatSession,
}) => {
	return (
		<ErrorBoundary fallbackComponent={<AppErrorFallback />}>
			<StoreProvider initialStateOverride={chatSession}>
				<App mvtConfig={mvtConfig} chatSession={chatSession} />
			</StoreProvider>
		</ErrorBoundary>
	);
};

export default hot(AppWithProviders);

setConfig({
	reloadHooks: false,
});
